import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Select,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import { IconAlertCircle, IconFileDollar, IconPlus } from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import {
  BONUS_CATEGORY_LABELS,
  DetailTable,
  EXPENSE_CATEGORY_LABELS,
  ScalePreviewTable,
  UPFRONT_PAY_TYPE_LABELS,
  bonusRows,
  expenseRows,
  percent,
  titleize,
  upfrontConfig,
} from "./repDealDisplay";

// The rep's current pay deal: the terms of the offer they're on, plus the entry
// point for sending them a new one.
function RepDealTab({ repId, repName }) {
  const navigate = useNavigate();
  const { getVerifiedJWT } = useAuth();

  const [deal, setDeal] = useState(null);
  const [sellingYears, setSellingYears] = useState([]);
  const [selectedYearId, setSelectedYearId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDeal = useCallback(
    async yearId => {
      try {
        setLoading(true);
        setError(null);
        const jwt = await getVerifiedJWT();
        if (!jwt) throw new Error("Authentication failed");

        const response = await fetch(
          getApiEndpoint(
            yearId
              ? `/v1/ps/user-deal/by-rep/${repId}?selling_year_id=${yearId}`
              : `/v1/ps/user-deal/by-rep/${repId}`
          ),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP ${response.status}`);
        }
        const result = await response.json();
        setDeal(result?.data ?? result ?? null);
      } catch (err) {
        setError(err.message || "Failed to load deal");
        setDeal(null);
      } finally {
        setLoading(false);
      }
    },
    [repId, getVerifiedJWT]
  );

  useEffect(() => {
    fetchDeal(null);
  }, [fetchDeal]);

  // The season picker's options; the deal read itself defaults to the rep's most
  // recent active deal when no year is passed.
  useEffect(() => {
    let cancelled = false;
    const fetchYears = async () => {
      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) return;
        const response = await fetch(getApiEndpoint("/v1/selling-year/list"), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        });
        if (!response.ok) return;
        const result = await response.json();
        const years = result?.data ?? result ?? [];
        if (!cancelled && Array.isArray(years)) setSellingYears(years);
      } catch (err) {
        // Without the year list the tab still shows the current deal.
      }
    };
    fetchYears();
    return () => {
      cancelled = true;
    };
  }, [getVerifiedJWT]);

  const dealUsers = useMemo(
    () => (Array.isArray(deal?.deal_users) ? deal.deal_users : []),
    [deal]
  );
  const hasDeal = deal?.deal_id != null;
  const isSplit = dealUsers.length > 1;

  const yearName = id =>
    sellingYears.find(y => String(y.id) === String(id))?.name || null;

  const header = (
    <Group justify="space-between" align="center" wrap="wrap">
      <Group gap="sm">
        <Text fw={700} size="lg">
          Deal
        </Text>
        {hasDeal && (
          <>
            <Badge color="orange" variant="light">
              {deal.deal_type === "MD" ? "Marketing Deal" : "Rep Deal"}
            </Badge>
            {isSplit && (
              <Badge color="gray" variant="light">
                Split
              </Badge>
            )}
          </>
        )}
      </Group>
      <Group gap="sm">
        {sellingYears.length > 0 && (
          <Select
            placeholder="Season"
            data={sellingYears.map(y => ({
              value: String(y.id),
              label: y.name,
            }))}
            value={selectedYearId}
            onChange={value => {
              setSelectedYearId(value);
              fetchDeal(value);
            }}
            clearable
            size="xs"
            w={150}
          />
        )}
        {/* A rep can only be on one deal per season — once they have one, the
            offer flow would be rejected server-side, so don't offer it. */}
        {!loading && !hasDeal && (
          <Button
            color="orange"
            leftSection={<IconPlus size={16} />}
            onClick={() => navigate(`/reps/${repId}/deal/add`)}
          >
            Add Deal
          </Button>
        )}
      </Group>
    </Group>
  );

  const renderBody = () => {
    if (loading) {
      return (
        <Center py="xl">
          <Loader />
        </Center>
      );
    }

    if (error) {
      return (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      );
    }

    if (!hasDeal) {
      return (
        <Card withBorder radius="md" p="xl">
          <Stack align="center" gap="xs">
            <IconFileDollar size={32} color="var(--mantine-color-gray-5)" />
            <Text fw={600}>No active deal</Text>
            <Text size="sm" c="dimmed" ta="center">
              {repName || "This rep"} has no deal for
              {selectedYearId
                ? ` ${yearName(selectedYearId) || "this season"}`
                : " the current season"}
              . Add a deal to send them an offer.
            </Text>
            <Button
              mt="sm"
              color="orange"
              leftSection={<IconPlus size={16} />}
              onClick={() => navigate(`/reps/${repId}/deal/add`)}
            >
              Add Deal
            </Button>
          </Stack>
        </Card>
      );
    }

    return (
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        {deal.deal_type === "MD" && deal.md_scale && (
          <ScalePreviewTable
            title="MD Scale"
            col2Label="MD %"
            lines={deal.md_scale.scale_lines}
          />
        )}

        {dealUsers.map((dealUser, index) => {
          const who = isSplit ? `Direct Seller ${index + 1}` : repName || "Rep";
          const upfront = dealUser.upfront_pay
            ? upfrontConfig(dealUser.upfront_pay)
            : null;
          return (
            <React.Fragment key={dealUser.id}>
              <ScalePreviewTable
                title={isSplit ? `${who} · Commission` : "Commission Scale"}
                subtitle={
                  isSplit ? percent(dealUser.deal_percentage) : undefined
                }
                col2Label="Commission"
                lines={dealUser.commission_scale?.scale_lines}
              />
              {upfront && (
                <DetailTable
                  title={
                    UPFRONT_PAY_TYPE_LABELS[
                      dealUser.upfront_pay.upfront_pay_type
                    ] || "Upfront Pay"
                  }
                  subtitle={isSplit ? who : undefined}
                  col1Label={upfront.col1}
                  col2Label={upfront.col2}
                  rows={upfront.rows}
                />
              )}
              {(dealUser.default_bonuses || []).map(bonus => (
                <DetailTable
                  key={bonus.id}
                  title={bonus.name || "Bonus"}
                  subtitle={isSplit ? who : undefined}
                  col1Label={
                    BONUS_CATEGORY_LABELS[bonus.bonus_type_category] || "Bonus"
                  }
                  col2Label="Detail"
                  rows={bonusRows(bonus)}
                />
              ))}
            </React.Fragment>
          );
        })}

        {deal.override_scale && (
          <ScalePreviewTable
            title="Override Scale"
            col2Label="Override %"
            lines={deal.override_scale.scale_lines}
          />
        )}

        {(deal.default_expenses || []).map(expense => (
          <DetailTable
            key={expense.id}
            title={expense.name || "Expense"}
            col1Label={
              EXPENSE_CATEGORY_LABELS[expense.expense_type_category] ||
              titleize(expense.expense_type_category)
            }
            col2Label="Detail"
            rows={expenseRows(expense)}
          />
        ))}
      </SimpleGrid>
    );
  };

  return (
    <Stack gap="lg" p="lg">
      {header}
      {renderBody()}
    </Stack>
  );
}

export default RepDealTab;
